import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { getLocalAdminToken, clearLocalAdminToken } from '@/hooks/useAdminAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAdmin() {
  const { isAuthenticated: auth0Authed, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect: _auth0Login, logout: _auth0Logout, user: auth0User } = useAuth0();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const localToken = getLocalAdminToken();
  const isAuthenticated = !!localToken || auth0Authed;

  useEffect(() => {
    if (!localToken && authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, localToken]);

  async function fetchMe() {
    try {
      let token;
      let url;
      if (localToken) { token = localToken; url = `${API}/auth/local/me`; }
      else { token = await getAccessTokenSilently(); url = `${API}/me`; }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user || data);
      } else if (localToken && res.status === 401) {
        clearLocalAdminToken();
        navigate('/admin/login');
      }
    } catch (e) {
      console.error('Failed to fetch me:', e);
    } finally { setLoading(false); }
  }

  function loginWithRedirect(opts) {
    if (localToken) return;
    // Always navigate to local admin login; only trigger Auth0 when a real
    // tenant is configured (prevents malformed https://authorize/ redirects).
    navigate('/admin/login');
    if (window.__EMINA_AUTH0_CONFIGURED__ && _auth0Login) {
      return _auth0Login(opts);
    }
  }

  function logout(opts) {
    if (localToken) { clearLocalAdminToken(); navigate('/admin/login'); return; }
    if (window.__EMINA_AUTH0_CONFIGURED__ && _auth0Logout) return _auth0Logout(opts);
    navigate('/');
  }

  const authFetch = useCallback(async (url, options = {}) => {
    const token = localToken || await getAccessTokenSilently();
    const headers = { ...options.headers, Authorization: `Bearer ${token}` };
    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, { ...options, headers });
  }, [getAccessTokenSilently, localToken]);

  // IMPORTANT: memoise the unified token accessor. If this is a fresh arrow on
  // every render, admin pages that use it as a dep for useCallback/useEffect
  // (e.g. HomepageSettings, StaticPageEdit) will re-fetch on every render and
  // overwrite the user's in-progress edits — symptom: input fields keep
  // resetting to the server state while typing.
  const getToken = useCallback(async () => (
    localToken || await getAccessTokenSilently()
  ), [localToken, getAccessTokenSilently]);

  return {
    user,
    isAdmin: user?.is_admin === true,
    isAuthenticated,
    isLoading: (localToken ? false : authLoading) || loading,
    loginWithRedirect,
    logout,
    auth0User,
    authFetch,
    isLocal: !!localToken,
    // Unified token accessor for admin pages — prefers local JWT
    getAccessTokenSilently: getToken,
  };
}

export function useAuthFetch() {
  const { getAccessTokenSilently } = useAuth0();
  return useCallback(async (url, options = {}) => {
    const local = getLocalAdminToken();
    const token = local || await getAccessTokenSilently();
    const headers = { ...options.headers, Authorization: `Bearer ${token}` };
    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, { ...options, headers });
  }, [getAccessTokenSilently]);
}
